import { useEffect } from 'react';

import { BackLink } from '@/components/BackLink';
import { Callout } from '@/components/Callout';
import { KioskCard } from '@/components/KioskCard';
import { KioskScreen } from '@/components/KioskScreen';
import { StatusBadge } from '@/components/StatusBadge';
import { useTranslation } from '@/i18n/useTranslation';
import { useKioskSession } from '@/lib/kioskSession';
import { HANDOFF_CONTROL } from '@/services/handoff.contract';
import { useBack } from '@/shared/hooks/useBack';
import { useFocusOnMount } from '@/shared/hooks/useFocusOnMount';

/**
 * The drug-test hand-off [decisions 0038 and 0129; Figma `13 · Drug test info — Desktop` (`12021:41168`) ·
 * `— iPad` (`12021:41152`) · `— Mobile` (`12021:41137`), read live on 2026-09-11].
 *
 * **It exists because tapping *Complete my drug test* used to land on `finish`**, whose no-submission
 * branch renders *"Please see a representative"* and a lone *Start over* — defect D1, reported by the
 * Operator on the running build: *«Cuando entro con un usuario y voy al menu de drug test em manda a esta
 * pantalla. No es la pantalla que deberia mostrar.»* The screen he expected is drawn, at all three widths,
 * and has been since 2026-08-28.
 *
 * **A DESTINATION, not a step** [0129]: no stepper, nothing to advance to, and the arrival menu is the only
 * route in — the design's own annotation on the frame (`12291:29555`) states that the I-9 → drug-test step
 * was removed from the flow.
 *
 * ## What it draws, and the two things it deliberately does not
 *
 * The frame is the I-9 screen's shape with one paragraph instead of a list: the 56px brand disc, the
 * heading, one line of body, one brand callout. Measured values live at the call sites below.
 *
 * ⛔ **No footer.** The frame's `Footer` holds a `Divider` and an `Actions` row that is EMPTY at all three
 * widths. That is not an omission: the prototype walkthrough — which governs flow, and which is four days
 * later than 0129 — says why in as many words. *"NO FOOTER (Tamara, 2026-09-03). This screen tells the
 * candidate to sit down, so it is a hand-off, not a step — and a hand-off screen cannot offer a way onward
 * or back: 'Back to options' contradicted the instruction we had just given them."* Nothing is built into
 * that row, and 0129's *"its call to action returns to the hub"* stays built as nothing.
 *
 * ⚠️ **Back, however, IS built — against the frame and against that ruling, on the Operator's explicit
 * instruction.** See the note at the control itself; it is the one place in this screen where the code
 * deliberately does not follow the design source, and it is written down so the next fidelity pass does not
 * "fix" it by deletion. **It was never a dead end even before** [BR-K7, 0046]: `KioskScreen` draws the
 * frame's own `Exit` control in the top bar.
 *
 * ## The queue entry
 *
 * **The promise is made here, so the entry is raised here** — `summonRepresentative()`, the single raiser,
 * idempotent for the candidate's session — naming the screen as its control, so the entry reads *drug test
 * in person* in *asked for help* [0462; KOP-1g scenario 3]. The body of this screen tells the candidate,
 * unconditionally and in ISS's own words, that *"a representative will call you by name shortly"*; the
 * Operator's rule for that sentence is exact: *«si le estamos diciendo que un representante la atendera y eso
 * nunca va a pasar porque no entra a la queue le estamos mintiendo»*. `AllDoneScreen` carries the same
 * sentence and raises an entry on the same event, for the same reason — with no control of its own.
 *
 * ⚠️ **The menu selection still raises nothing of its own** — `queuesForReason()` is untouched, and the
 * `POST /arrival-reason` it drives writes to `kiosk:arrival:*`, which the recruiter queue does not read
 * [defect D9]. The entry a recruiter actually sees is written from this screen and from nowhere else, which
 * is what keeps *"exactly one"* true without a flag on the selection.
 *
 * ⏳ **Whether an ASK is required before the promise is OQ-110's**, and the frame answers it for this
 * screen by drawing no control that could carry one: the only help affordance on it is the global
 * *Need help?* pill, which is inert by design until KQA-1's panel exists [`NeedHelpFab`]. Choosing
 * *Complete my drug test* is the ask this screen was reached by.
 */
export function DrugTestScreen() {
  const t = useTranslation();
  const back = useBack();
  const heading = useFocusOnMount<HTMLHeadingElement>();
  const summonRepresentative = useKioskSession((state) => state.summonRepresentative);

  useEffect(() => {
    summonRepresentative(undefined, HANDOFF_CONTROL.drugTestScreen);
  }, [summonRepresentative]);

  return (
    <KioskScreen>
      {/*
        No `footer` prop: the frame's footer `Actions` row is EMPTY at all three widths and nothing is
        invented into it — see the note on this component.
      */}
      <KioskCard
        header={
          <>
            {/*
              ⛔ **THIS CONTROL CONTRADICTS ITS OWN DESIGN SOURCE, AND IT IS MEANT TO. DO NOT DELETE IT AS
              DRIFT.** Added on the Operator's explicit instruction, 2026-09-11: *«lo de drug test esta bien
              es una pantalla que al entrar ya te encola, lo que si dejale el boton back que veo que no esta
              incluido alli»*.

              It overrides two things, both of them still true as written:

              1. **The frame.** Inside the `Header` instance of `12021:41168` (Desktop) the `Back button`
                 node is `visible: false`, as is `Action link`; same at iPad `12021:41152` and Mobile
                 `12021:41137`. Read live on 2026-09-11. The design does not draw this control.
              2. **A recorded design ruling.** *"NO FOOTER (Tamara, 2026-09-03) … a hand-off screen cannot
                 offer a way onward or back."* The footer is still not built; this Back is exactly the part
                 of that ruling the Operator overrode.

              So a design-fidelity pass on `13 · Drug test info` WILL report this as an element the frame
              does not have. It is a recorded departure, not a defect: revisit it when the design file
              adds a Back control to the frame, or when the Operator withdraws the instruction.

              Where it goes is his other ruling of the same day — the arrival menu, *What brings you in
              today*, as for the I-9 screen's *No, someone else will*. `useBack()` is the shared answer
              [see the hook]: the menu is this screen's only entrance, so its `arrival` is always set and
              Back lands on `arrivalReason`. Going back raises NO second queue entry and neither does
              returning — `summonRepresentative()` is once per candidate SESSION, and `goTo` does not start
              a new one.
            */}
            <BackLink onClick={back} />
            <StatusBadge label={t('badge.kiosk')} />
          </>
        }
      >
        {/*
          `Main`, measured at all three widths: `layoutMode: VERTICAL`, `itemSpacing: 12` (`gap-3`),
          `paddingBottom: 40` (`pb-10`), `counterAxisAlignItems: CENTER`. The 448 content column and the
          24px side inset at 375 belong to `KioskCard`, which is why neither is repeated here.
        */}
        {/*
          `text-center` is on the heading and the body, NOT on this container: the frame's callout text is
          `textAlignHorizontal: LEFT` at all three widths, and centring it here would have inherited down
          into it. Same split `I9Screen` uses, and for the same reason.
        */}
        <div className="flex w-full flex-col items-center gap-3 pb-10">
          <span
            aria-hidden="true"
            className="flex size-14 items-center justify-center rounded-[var(--radius-full)] bg-[var(--primary-muted)]"
          >
            {/*
              The conical flask, in the frame's own geometry: the two vectors of `12021:41176` measure
              9 × 0 and 14.14 × 17 inside a 24 box, stroked 1.8 with round caps and joins, in `--primary`
              (#03A5B6 — the frame's stroke, read live). The path below reproduces those points exactly;
              it is NOT the 20px glyph the arrival menu's row uses, which is the same icon drawn for a
              smaller box.
            */}
            <svg
              fill="none"
              height="24"
              stroke="var(--primary)"
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth="1.8"
              viewBox="0 0 24 24"
              width="24"
            >
              <path d="M9 3h6" />
              <path d="M10 3v5.5l-4.8 8.5a2 2 0 0 0 1.8 3h10a2 2 0 0 0 1.8-3L14 8.5V3" />
            </svg>
          </span>
          {/* 24/700, 28px line, −0.36px tracking, `#000` — the frame's heading style at all three widths. */}
          <h1
            className="kiosk-heading-focus w-full text-center text-2xl font-bold leading-7 tracking-[-0.36px] text-black"
            ref={heading}
            tabIndex={-1}
          >
            {t('drugTest.heading')}
          </h1>
          {/* 14/400, 24px line, `#585756` = `--muted-foreground`. */}
          <p className="w-full text-center text-sm leading-6 text-[var(--muted-foreground)]">
            {t('drugTest.body')}
          </p>
          {/*
            The frame's `Callout` instance, brand tone: `--primary-muted` fill, 8px radius, 16px padding,
            14/20 text — which is the component's own default, so nothing is overridden here.
          */}
          <Callout>{t('drugTest.callout')}</Callout>
        </div>
      </KioskCard>
    </KioskScreen>
  );
}
